import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useClientPortalPaths } from "../../hooks/useClientPortalBase";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import {
  createVendorDirectoryImportJob,
  getVendorDirectoryImportJob,
  uploadVendorDirectoryImportFile,
  inspectVendorDirectoryImportFile,
  saveVendorDirectoryImportMapping,
  validateVendorDirectoryImport,
  markVendorDirectoryImportReady,
  startVendorDirectoryImportProcessing,
  cancelVendorDirectoryImportJob,
  retryVendorDirectoryImportJob,
  rollbackVendorDirectoryImportJob,
  listVendorDirectorySources,
  listVendorDirectoryImportCandidates,
  vendorDirectoryImportErrorsExportUrl,
} from "../../api/clientVendorDirectoryImportApi";
import ClientVendorDirectorySubnav from "../../components/casamap/admin/ClientVendorDirectorySubnav";
import VendorDirectoryPermissionGate from "../../components/casamap/admin/VendorDirectoryPermissionGate";
import { VENDOR_DIRECTORY_PERMISSIONS } from "../../auth/casamapaVendorDirectoryPermissions";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { formatClientDate, formatStatusLabel } from "../../utils/clientPortalFormat";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const MAPPING_TARGETS = [
  "display_name",
  "legal_name",
  "vendor_kind",
  "category",
  "service",
  "phone",
  "whatsapp",
  "email",
  "website",
  "address",
  "municipality",
  "neighborhood",
  "description",
  "source_record_id",
];

const STEPS = ["Fuente", "Archivo", "Inspección", "Mapeo", "Vista previa", "Procesar", "Resultados"];

function resolveStep(job) {
  if (!job) return 0;
  if (["completed", "completed_with_errors", "failed", "rolled_back"].includes(job.status)) return 6;
  if (job.status === "processing") return 6;
  if (job.status === "ready") return 5;
  if (job.status === "validating") return 4;
  if (job.status === "mapping") return 3;
  if (job.status === "uploaded") return 2;
  return job.sourceId ? 1 : 0;
}

export default function ClientVendorDirectoryImportPage({ isNew = false }) {
  const { clientViewId, importJobId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const navigate = useNavigate();
  const paths = useClientPortalPaths();
  const { activeView } = useOutletContext() || {};
  const basePath = `${serviciosBase}/importaciones`;

  const [job, setJob] = useState(null);
  const [sources, setSources] = useState([]);
  const [inspection, setInspection] = useState(null);
  const [preview, setPreview] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [message, setMessage] = useState("");

  const [sourceId, setSourceId] = useState("");
  const [importNotes, setImportNotes] = useState("");
  const [headerRow, setHeaderRow] = useState(1);
  const [delimiter, setDelimiter] = useState("");
  const [columnMappings, setColumnMappings] = useState({});
  const [defaultCategory, setDefaultCategory] = useState("");
  const [defaultMunicipality, setDefaultMunicipality] = useState("");

  const currentStep = useMemo(() => resolveStep(job), [job]);

  const loadJob = useCallback(async () => {
    if (isNew || !importJobId) return;
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getVendorDirectoryImportJob(clientViewId, importJobId);
      setJob(body.job);
      const mapping = body.job?.mappingConfig || {};
      setHeaderRow(mapping.headerRow ?? 1);
      setDelimiter(mapping.delimiter || "");
      setColumnMappings(mapping.columnMappings || {});
      setDefaultCategory(mapping.defaults?.category || "");
      setDefaultMunicipality(mapping.defaults?.municipality || "");
      if (["completed", "completed_with_errors", "processing"].includes(body.job?.status)) {
        const candBody = await listVendorDirectoryImportCandidates(clientViewId, importJobId, {
          page: 1,
          pageSize: 10,
        });
        setCandidates(candBody.candidates || []);
      }
    } catch (error) {
      setErrorMessage(error.message || "No se pudo cargar la importación.");
    } finally {
      setLoading(false);
    }
  }, [clientViewId, importJobId, isNew]);

  const loadSources = useCallback(async () => {
    try {
      const body = await listVendorDirectorySources(clientViewId);
      setSources(body.sources || []);
    } catch {
      setSources([]);
    }
  }, [clientViewId]);

  useEffect(() => {
    loadSources();
  }, [loadSources]);

  useEffect(() => {
    loadJob();
  }, [loadJob]);

  useEffect(() => {
    if (!isNew && job?.status === "processing") {
      const timer = setInterval(loadJob, 3000);
      return () => clearInterval(timer);
    }
    return undefined;
  }, [isNew, job?.status, loadJob]);

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!sourceId) {
      setErrorMessage("Seleccione una fuente.");
      return;
    }
    setBusy(true);
    setErrorMessage("");
    try {
      const body = await createVendorDirectoryImportJob(clientViewId, {
        sourceId,
        importType: "csv",
        importNotes,
      });
      navigate(`${basePath}/${body.job.id}`, { replace: true });
    } catch (error) {
      setErrorMessage(error.message || "No se pudo crear la importación.");
    } finally {
      setBusy(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !importJobId) return;
    setBusy(true);
    setErrorMessage("");
    try {
      const body = await uploadVendorDirectoryImportFile(clientViewId, importJobId, file);
      setJob(body.job);
      setMessage("Archivo cargado correctamente.");
    } catch (error) {
      setErrorMessage(error.message || "Error al cargar el archivo.");
    } finally {
      setBusy(false);
      event.target.value = "";
    }
  };

  const handleInspect = async () => {
    setBusy(true);
    setErrorMessage("");
    try {
      const body = await inspectVendorDirectoryImportFile(clientViewId, importJobId, {
        headerRow,
        delimiter: delimiter || undefined,
      });
      setInspection(body);
    } catch (error) {
      setErrorMessage(error.message || "No se pudo inspeccionar el archivo.");
    } finally {
      setBusy(false);
    }
  };

  const handleSaveMapping = async () => {
    setBusy(true);
    setErrorMessage("");
    try {
      const body = await saveVendorDirectoryImportMapping(clientViewId, importJobId, {
        columnMappings,
        headerRow,
        delimiter: delimiter || null,
        defaults: {
          category: defaultCategory || null,
          municipality: defaultMunicipality || null,
        },
      });
      setJob(body.job);
      setMessage("Mapeo guardado.");
    } catch (error) {
      setErrorMessage(error.message || "No se pudo guardar el mapeo.");
    } finally {
      setBusy(false);
    }
  };

  const handleValidate = async () => {
    setBusy(true);
    setErrorMessage("");
    try {
      const body = await validateVendorDirectoryImport(clientViewId, importJobId);
      setPreview(body);
      setJob((prev) => (prev ? { ...prev, status: "validating", validationSummary: body.summary } : prev));
    } catch (error) {
      setErrorMessage(error.message || "Validación fallida.");
    } finally {
      setBusy(false);
    }
  };

  const handleMarkReady = async () => {
    setBusy(true);
    try {
      const body = await markVendorDirectoryImportReady(clientViewId, importJobId);
      setJob(body.job);
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleProcess = async () => {
    setBusy(true);
    setErrorMessage("");
    try {
      const body = await startVendorDirectoryImportProcessing(clientViewId, importJobId);
      setJob(body.job);
      setMessage("Procesamiento iniciado. Los candidatos aparecerán al finalizar.");
    } catch (error) {
      setErrorMessage(error.message || "No se pudo iniciar el procesamiento.");
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("¿Cancelar esta importación?")) return;
    setBusy(true);
    try {
      const body = await cancelVendorDirectoryImportJob(clientViewId, importJobId);
      setJob(body.job);
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleRetry = async () => {
    setBusy(true);
    try {
      const body = await retryVendorDirectoryImportJob(clientViewId, importJobId);
      setJob(body.job);
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleRollback = async () => {
    if (!window.confirm("¿Revertir candidatos de staging de esta importación?")) return;
    setBusy(true);
    try {
      const body = await rollbackVendorDirectoryImportJob(clientViewId, importJobId);
      setJob(body.job);
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setBusy(false);
    }
  };

  if (isNew) {
    return (
      <div className="page-content cvd-admin">
        <ClientVendorDirectorySubnav clientViewId={clientViewId} />
        <div className="cvd-admin__header">
          <div>
            <h1 className="cvd-admin__title">Nueva importación</h1>
            <p className="lede">Seleccione la fuente de datos antes de cargar un archivo.</p>
          </div>
          <Link className="btn btn-secondary" to={basePath}>
            Volver
          </Link>
        </div>
        {errorMessage && <ClientPortalError message={errorMessage} />}
        <form className="cvd-section" onSubmit={handleCreate}>
          <div className="cvd-field">
            <label htmlFor="import-source">Fuente *</label>
            <select
              id="import-source"
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
              required
            >
              <option value="">Seleccionar…</option>
              {sources.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.sourceType})
                </option>
              ))}
            </select>
          </div>
          <div className="cvd-field">
            <label htmlFor="import-notes">Notas de importación</label>
            <textarea
              id="import-notes"
              rows={3}
              value={importNotes}
              onChange={(e) => setImportNotes(e.target.value)}
            />
          </div>
          <p className="lede">
            Formatos admitidos: CSV y TSV (máx. 15 MB, 25 000 filas). Los registros importados no se
            publican automáticamente.
          </p>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? "Creando…" : "Continuar"}
          </button>
        </form>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-content cvd-admin">
        <ClientPortalLoading message="Cargando importación…" />
      </div>
    );
  }

  if (errorMessage && !job) {
    return (
      <div className="page-content cvd-admin">
        <ClientPortalError message={errorMessage} onRetry={loadJob} />
      </div>
    );
  }

  const columns =
    inspection?.columns?.length > 0
      ? inspection.columns
      : preview?.rows?.[0]?.raw
        ? Object.keys(preview.rows[0].raw)
        : [];

  return (
    <div className="page-content cvd-admin">
      <ClientVendorDirectorySubnav clientViewId={clientViewId} />
      <div className="cvd-admin__header">
        <div>
          <h1 className="cvd-admin__title">{job?.originalFilename || "Importación"}</h1>
          <p className="lede">
            {activeView?.name} ·{" "}
            <span className={`cvd-badge cvd-badge--draft`}>{formatStatusLabel(job?.status)}</span>
          </p>
        </div>
        <Link className="btn btn-secondary" to={basePath}>
          Lista
        </Link>
      </div>

      <div className="cvd-steps" aria-label="Pasos de importación">
        {STEPS.map((label, index) => (
          <span
            key={label}
            className={`cvd-step${index === currentStep ? " cvd-step--active" : ""}${
              index < currentStep ? " cvd-step--done" : ""
            }`}
          >
            {index + 1}. {label}
          </span>
        ))}
      </div>

      {message && (
        <p className="lede" role="status">
          {message}
        </p>
      )}
      {errorMessage && <ClientPortalError message={errorMessage} />}

      <section className="cvd-section">
        <h2>Archivo</h2>
        <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.IMPORTS_CREATE}>
          <div className="cvd-field">
            <label htmlFor="import-file">Cargar CSV o TSV</label>
            <input
              id="import-file"
              type="file"
              accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values"
              onChange={handleUpload}
              disabled={busy || job?.status === "processing"}
            />
          </div>
        </VendorDirectoryPermissionGate>
        {job?.originalFilename && (
          <p>
            Archivo: <strong>{job.originalFilename}</strong> · Creado{" "}
            {formatClientDate(job.createdAt)}
          </p>
        )}
      </section>

      {job?.storageKey && (
        <section className="cvd-section">
          <h2>Inspección</h2>
          <div className="cvd-field-grid">
            <div className="cvd-field">
              <label htmlFor="header-row">Fila de encabezado</label>
              <input
                id="header-row"
                type="number"
                min={1}
                value={headerRow}
                onChange={(e) => setHeaderRow(Number(e.target.value))}
              />
            </div>
            <div className="cvd-field">
              <label htmlFor="delimiter">Delimitador</label>
              <select
                id="delimiter"
                value={delimiter}
                onChange={(e) => setDelimiter(e.target.value)}
              >
                <option value="">Automático</option>
                <option value=",">Coma (,)</option>
                <option value=";">Punto y coma (;)</option>
                <option value="\t">Tabulador</option>
              </select>
            </div>
          </div>
          <button type="button" className="btn btn-secondary" onClick={handleInspect} disabled={busy}>
            Inspeccionar archivo
          </button>
          {inspection && (
            <div className="cvd-summary-grid" style={{ marginTop: "1rem" }}>
              <div className="cvd-summary-card">
                <strong>{inspection.rowCount}</strong>
                <span>Filas</span>
              </div>
              <div className="cvd-summary-card">
                <strong>{inspection.columnCount}</strong>
                <span>Columnas</span>
              </div>
              <div className="cvd-summary-card">
                <strong>{inspection.encoding}</strong>
                <span>Codificación</span>
              </div>
            </div>
          )}
        </section>
      )}

      {(inspection?.columns?.length || columns.length > 0) && (
        <section className="cvd-section">
          <h2>Mapeo de columnas</h2>
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.IMPORTS_CONFIGURE}>
            <div className="cvd-field-grid">
              <div className="cvd-field">
                <label htmlFor="default-category">Categoría por defecto</label>
                <input
                  id="default-category"
                  value={defaultCategory}
                  onChange={(e) => setDefaultCategory(e.target.value)}
                  placeholder="plumbing"
                />
              </div>
              <div className="cvd-field">
                <label htmlFor="default-municipality">Municipio por defecto</label>
                <input
                  id="default-municipality"
                  value={defaultMunicipality}
                  onChange={(e) => setDefaultMunicipality(e.target.value)}
                  placeholder="medellin"
                />
              </div>
            </div>
            <div className="cvd-table-wrap">
              <table className="cvd-table">
                <thead>
                  <tr>
                    <th>Columna origen</th>
                    <th>Campo destino</th>
                  </tr>
                </thead>
                <tbody>
                  {(inspection?.columns || columns).map((col) => (
                    <tr key={col}>
                      <td>{col}</td>
                      <td>
                        <select
                          value={columnMappings[col] || ""}
                          onChange={(e) =>
                            setColumnMappings((prev) => ({
                              ...prev,
                              [col]: e.target.value || undefined,
                            }))
                          }
                          aria-label={`Mapear ${col}`}
                        >
                          <option value="">— Ignorar —</option>
                          {MAPPING_TARGETS.map((target) => (
                            <option key={target} value={target}>
                              {target}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button type="button" className="btn btn-secondary" onClick={handleSaveMapping} disabled={busy}>
              Guardar mapeo
            </button>
          </VendorDirectoryPermissionGate>
        </section>
      )}

      <section className="cvd-section">
        <h2>Vista previa y validación</h2>
        <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.IMPORTS_VIEW}>
          <button type="button" className="btn btn-secondary" onClick={handleValidate} disabled={busy}>
            Validar filas
          </button>
          {(preview?.summary || job?.validationSummary) && (
            <div className="cvd-summary-grid" style={{ marginTop: "1rem" }}>
              {Object.entries(preview?.summary || job.validationSummary).map(([key, value]) => (
                <div key={key} className="cvd-summary-card">
                  <strong>{value}</strong>
                  <span>{key}</span>
                </div>
              ))}
            </div>
          )}
          {preview?.rows?.length > 0 && (
            <div className="cvd-table-wrap" style={{ marginTop: "1rem" }}>
              <table className="cvd-table">
                <thead>
                  <tr>
                    <th>Fila</th>
                    <th>Nombre</th>
                    <th>Errores</th>
                    <th>Categoría</th>
                    <th>Elegible</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.slice(0, 50).map((row) => (
                    <tr key={row.rowNumber}>
                      <td>{row.rowNumber}</td>
                      <td>{row.mapped?.display_name || row.mapped?.legal_name || "—"}</td>
                      <td>{row.errors?.map((e) => e.message).join("; ") || "—"}</td>
                      <td>{row.categoryMatch?.status || "—"}</td>
                      <td>{row.eligible ? "Sí" : "No"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </VendorDirectoryPermissionGate>
      </section>

      <section className="cvd-section">
        <h2>Procesamiento</h2>
        <p className="lede">
          El procesamiento crea candidatos en el área de staging. No publica proveedores en el
          directorio.
        </p>
        <div className="cvd-risk-actions">
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.IMPORTS_CONFIGURE}>
            <button type="button" className="btn btn-secondary" onClick={handleMarkReady} disabled={busy}>
              Marcar listo
            </button>
          </VendorDirectoryPermissionGate>
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.IMPORTS_EXECUTE}>
            <button type="button" className="btn btn-primary" onClick={handleProcess} disabled={busy}>
              Procesar importación
            </button>
          </VendorDirectoryPermissionGate>
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.IMPORTS_CANCEL}>
            <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={busy}>
              Cancelar
            </button>
          </VendorDirectoryPermissionGate>
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.IMPORTS_RETRY}>
            <button type="button" className="btn btn-secondary" onClick={handleRetry} disabled={busy}>
              Reintentar
            </button>
          </VendorDirectoryPermissionGate>
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.IMPORTS_ROLLBACK}>
            <button type="button" className="btn btn-secondary" onClick={handleRollback} disabled={busy}>
              Revertir staging
            </button>
          </VendorDirectoryPermissionGate>
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.IMPORTS_EXPORT_ERRORS}>
            <a
              className="btn btn-secondary"
              href={vendorDirectoryImportErrorsExportUrl(clientViewId, importJobId)}
              download
            >
              Exportar errores
            </a>
          </VendorDirectoryPermissionGate>
        </div>
        {job?.status === "processing" && (
          <p role="status" aria-live="polite">
            Procesando… {job.processingCheckpoint?.rowsProcessed ?? 0} filas procesadas.
          </p>
        )}
      </section>

      {candidates.length > 0 && (
        <section className="cvd-section">
          <h2>Candidatos generados</h2>
          <div className="cvd-table-wrap">
            <table className="cvd-table">
              <thead>
                <tr>
                  <th>Fila</th>
                  <th>Nombre</th>
                  <th>Estado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {candidates.map((c) => (
                  <tr key={c.id}>
                    <td>{c.sourceRowNumber}</td>
                    <td>{c.rawName || c.displayName}</td>
                    <td>{formatStatusLabel(c.status)}</td>
                    <td>
                      <Link to={`${serviciosBase}/candidatos/${c.id}`}>
                        Revisar
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Link to={`${serviciosBase}/candidatos?importJobId=${importJobId}`}>
            Ver todos los candidatos
          </Link>
        </section>
      )}
    </div>
  );
}
